import { useCallback, useEffect, useRef, useState } from 'react'
import { supabase } from '../lib/supabase'
import { onAppRevalidate } from '../lib/revalidate'
import { onNavBadgesStale } from '../lib/navBadges'
import { countUnread, isPreviewMode } from '../lib/messaging'
import { getTiktokSampleCount } from '../lib/api'

// The three sidebar nav counts, in ONE request.
//
// Each badge used to own its own effect, its own fetch and its own focus
// listener, so painting the nav cost four requests:
//
//   cr_applications             count of pending
//   tiktok_sample_applications  count of pending (via the worker)
//   cr_conversation_reads       EVERY read cursor for the user    (unbounded)
//   cr_conversations            EVERY active thread for the brand (unbounded)
//
// The last two are the expensive pair — two unbounded selects shipped to the
// browser so it could compute one integer. The brand_sidebar_badges RPC does
// all three counts in the database and returns a single row.
//
// A brand with no realtime activity now makes ONE request per refresh instead
// of four, and the unread count no longer scales with the size of the inbox.

export interface SidebarBadges {
  applications: number | null
  samples: number | null
  unread: number
  supportUnread: number
}

const EMPTY: SidebarBadges = { applications: null, samples: null, unread: 0, supportUnread: 0 }

/** How stale a nav badge may get before a genuine tab return refetches it.
 *  One request now, so this can be generous without being wasteful. */
const BADGE_REVALIDATE_MS = 60_000

interface BadgeRow {
  applications_pending: number | string | null
  samples_pending: number | string | null
  unread_conversations: number | string | null
  support_unread: number | string | null
}

// COUNT() comes back as bigint, which PostgREST serialises as a string.
const toCount = (v: number | string | null | undefined): number => {
  const n = typeof v === 'string' ? parseInt(v, 10) : v
  return Number.isFinite(n as number) ? (n as number) : 0
}

/**
 * True when the RPC simply is not there yet — the migration has not been
 * applied to this environment. Postgres answers an unknown function with
 * PGRST202 (schema-cache miss) or 42883 (undefined_function). Anything else is
 * a real error and must NOT silently trigger the legacy path, or a broken
 * query would quietly cost four requests forever.
 */
function isMissingFunction(err: { code?: string; message?: string } | null): boolean {
  if (!err) return false
  if (err.code === 'PGRST202' || err.code === '42883') return true
  return /Could not find the function|function .* does not exist/i.test(err.message ?? '')
}

/** The pre-RPC path, kept only for the window where the migration has not
 *  landed. Deleting it is safe once brand_sidebar_badges exists everywhere. */
async function legacyBadges(brandId: string, userId: string | null): Promise<SidebarBadges> {
  const [apps, samples, unread] = await Promise.all([
    supabase
      .from('cr_applications')
      .select('id', { count: 'exact', head: true })
      .eq('brand_id', brandId)
      .eq('status', 'pending')
      .then(r => r.count ?? 0, () => 0),
    getTiktokSampleCount(brandId, 'PENDING').catch(() => 0),
    userId ? countUnread(brandId, userId).catch(() => 0) : Promise.resolve(0),
  ])
  // support_unread has no pre-RPC fallback query — it's only ever computed by
  // the RPC itself. Fine for the same reason the rest of this path is fine:
  // it's a temporary window before the migration lands, not a permanent path.
  return { applications: apps, samples, unread, supportUnread: 0 }
}

export function useSidebarBadges(
  brandId: string | undefined,
  userId: string | null,
  opts: { messagesEnabled: boolean },
): SidebarBadges {
  const [badges, setBadges] = useState<SidebarBadges>(EMPTY)
  const { messagesEnabled } = opts
  // Once the RPC is known to be missing, stop paying for a failed call on every
  // refresh — go straight to the legacy path for the rest of the session.
  const rpcMissing = useRef(false)

  const load = useCallback(async (): Promise<SidebarBadges | null> => {
    if (!brandId) return null
    if (isPreviewMode()) return { applications: null, samples: null, unread: 2, supportUnread: 0 }

    if (!rpcMissing.current) {
      const { data, error } = await supabase
        .rpc('brand_sidebar_badges', { p_brand_id: brandId })
        .maybeSingle()
      if (!error) {
        const row = (data ?? null) as BadgeRow | null
        return {
          applications: toCount(row?.applications_pending),
          samples: toCount(row?.samples_pending),
          unread: toCount(row?.unread_conversations),
          supportUnread: toCount(row?.support_unread),
        }
      }
      if (!isMissingFunction(error)) return null   // real failure: keep what we have
      rpcMissing.current = true
    }
    return legacyBadges(brandId, userId)
  }, [brandId, userId])

  useEffect(() => {
    if (!brandId) { setBadges(EMPTY); return }
    let cancelled = false

    const refresh = () => {
      void load().then(next => {
        if (!cancelled && next) setBadges(next)
      })
    }

    refresh()
    const off = onAppRevalidate(refresh, BADGE_REVALIDATE_MS)
    // The user changing a count themselves — reading a thread, approving an
    // application, reviewing a sample — produces no realtime event this badge
    // can see (a read writes cr_conversation_reads; the other two go through
    // the worker). Without this the badge kept claiming "1" after the user had
    // already dealt with it. Unthrottled on purpose: it fires only on a
    // deliberate action, and the whole point is that it is immediate.
    const offStale = onNavBadgesStale(refresh)

    // Realtime is the primary signal for the unread badge: a creator message
    // bumps last_message_at on the thread via trigger, so the badge moves
    // without waiting for a refresh. The focus revalidation above is only the
    // backstop for events missed while the tab was away. Support tickets are
    // not gated by messagesEnabled — an admin reply should light up the
    // Support nav item regardless of whether brand<->creator messaging is on.
    if (isPreviewMode() || (!messagesEnabled && !userId)) {
      return () => { cancelled = true; off(); offStale() }
    }
    const channel = supabase.channel(`sidebar_badges_${brandId}`)
    if (messagesEnabled) {
      channel.on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'cr_conversations', filter: `brand_id=eq.${brandId}` },
        refresh,
      )
    }
    if (userId) {
      channel.on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'support_conversations', filter: `requester_user_id=eq.${userId}` },
        refresh,
      )
    }
    channel.subscribe()

    return () => {
      cancelled = true
      off()
      offStale()
      supabase.removeChannel(channel)
    }
  }, [brandId, load, messagesEnabled, userId])

  return badges
}
